const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
const TOKEN_KEY = 'nh_auth_token';

class ApiService {
  // Callback for handling auth errors (set by AuthContext)
  onAuthError = null;

  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const token = this.getToken();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    // GH-139: callers using request() directly sometimes pass a raw object as
    // body, expecting it to be stringified. Native fetch coerces objects to
    // '[object Object]', which the backend body-parser then rejects with a
    // confusing 400 "[object Object]" is not valid JSON error. Stringify
    // automatically when the body is a non-string, non-FormData, non-Blob
    // value with a JSON content-type. FormData/Blob callers (file uploads)
    // are left alone — they need raw bodies.
    if (
      config.body
      && typeof config.body !== 'string'
      && typeof FormData !== 'undefined' && !(config.body instanceof FormData)
      && typeof Blob !== 'undefined' && !(config.body instanceof Blob)
      && (config.headers['Content-Type'] || '').includes('application/json')
    ) {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);
      // GH-126: response body may be HTML (gateway timeout / ALB 504 page)
      // or empty. Read as text first so we can wrap the JSON parse failure
      // with the actual status + body preview instead of surfacing V8's
      // confusing "Unexpected token '<', '<html>..." JSON parse error
      // straight through to the user.
      const bodyText = await response.text();
      let data;
      try {
        data = bodyText ? JSON.parse(bodyText) : null;
      } catch (parseErr) {
        const err = new Error(
          `Server returned a non-JSON response (HTTP ${response.status}). Likely a gateway timeout or proxy error. First 200 chars: ${bodyText.slice(0, 200)}`
        );
        err.status = response.status;
        err.body = bodyText;
        err.parseError = parseErr.message;
        throw err;
      }

      // Handle 401 Unauthorized - trigger logout
      if (response.status === 401) {
        if (this.onAuthError) {
          this.onAuthError();
        }
        throw new Error(data?.error || 'Authentication required');
      }

      if (!response.ok) {
        // data?.error may be a string from our controllers, or an object if
        // some upstream proxy returned an unusual JSON shape. Coerce so the
        // Error message is always a string instead of '[object Object]'.
        let msg = 'Request failed';
        if (typeof data?.error === 'string') msg = data.error;
        else if (data?.error) msg = JSON.stringify(data.error);
        else if (typeof data === 'string') msg = data;
        const err = new Error(msg);
        err.status = response.status;
        err.body = data;
        if (data && typeof data.error_code === 'string') err.code = data.error_code;
        throw err;
      }

      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Generic HTTP methods for flexibility
  async get(endpoint) {
    return this.request(endpoint);
  }

  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // Helper to build query string with practiceId
  buildQuery(params = {}) {
    const query = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        query.append(key, value);
      }
    });
    const queryString = query.toString();
    return queryString ? `?${queryString}` : '';
  }

  // Dashboard & Reports
  async getDashboardStats(practiceId = null) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/reports/dashboard${query}`);
  }

  async getCampaignPerformance(campaignType = null, practiceId = null) {
    const query = this.buildQuery({ campaignType, practice_id: practiceId });
    return this.request(`/reports/campaign-performance${query}`);
  }

  async getMessagingStats(practiceId = null) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/reports/messaging-stats${query}`);
  }

  async getRecentActivity(limit = 50, practiceId = null) {
    const query = this.buildQuery({ limit, practice_id: practiceId });
    return this.request(`/reports/activity${query}`);
  }

  async getPracticeAuditLog(practiceId, params = {}) {
    const qs = Object.entries(params).filter(([, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
    return this.request(`/practices/${practiceId}/audit-log${qs ? '?' + qs : ''}`);
  }

  async getRevenueRecovery(startDate = null, endDate = null, practiceId = null) {
    const query = this.buildQuery({ startDate, endDate, practice_id: practiceId });
    return this.request(`/reports/revenue-recovery${query}`);
  }

  // commons #227
  async getAppointmentSourceReport(practiceId, { startDate, endDate, providerId, appointmentType, status } = {}) {
    const query = this.buildQuery({ practice_id: practiceId, startDate, endDate, providerId, appointmentType, status });
    return this.request(`/reports/appointment-source${query}`);
  }

  async getOptOutReport(practiceId = null) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/reports/opt-outs${query}`);
  }

  async getCampaignTimeline(days = 30, practiceId = null) {
    const query = this.buildQuery({ days, practice_id: practiceId });
    return this.request(`/reports/timeline${query}`);
  }

  // Campaigns
  async runAllCampaigns(practiceId = null) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/campaigns/run${query}`, { method: 'POST' });
  }

  async runCampaign(campaignType, practiceId = null) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/campaigns/${campaignType}/run${query}`, { method: 'POST' });
  }

  async getEnrollments(campaignType = null, status = null, practiceId = null, startDate = null, endDate = null) {
    const query = this.buildQuery({ campaignType, status, practice_id: practiceId, startDate, endDate });
    return this.request(`/campaigns/enrollments${query}`);
  }

  async getEnrollmentById(id) {
    return this.request(`/campaigns/enrollments/${id}`);
  }

  async exitEnrollment(id, reason) {
    return this.request(`/campaigns/enrollments/${id}/exit`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async handleBooking(patientId, appointmentValue) {
    return this.request('/campaigns/booking', {
      method: 'POST',
      body: JSON.stringify({ patientId, appointmentValue }),
    });
  }

  async handleOptOut(patientId) {
    return this.request('/campaigns/opt-out', {
      method: 'POST',
      body: JSON.stringify({ patientId }),
    });
  }

  async getCampaignConfigs(practiceId = null) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/campaigns/configs${query}`);
  }

  async updateCampaignConfig(campaignType, config, practiceId = null) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/campaigns/configs/${campaignType}${query}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  async getCampaignStats(campaignType = null, practiceId = null) {
    const query = this.buildQuery({ campaignType, practice_id: practiceId });
    return this.request(`/campaigns/stats${query}`);
  }

  // Campaign Types (Dynamic Campaign Configuration)
  async getCampaignTypes(practiceId, includeInactive = false) {
    const query = this.buildQuery({ practice_id: practiceId, include_inactive: includeInactive });
    return this.request(`/campaign-types${query}`);
  }

  async getCampaignType(id) {
    return this.request(`/campaign-types/${id}`);
  }

  async createCampaignType(data) {
    return this.request('/campaign-types', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaignType(id, data) {
    return this.request(`/campaign-types/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCampaignType(id) {
    return this.request(`/campaign-types/${id}`, { method: 'DELETE' });
  }

  async activateCampaignType(id) {
    return this.request(`/campaign-types/${id}/activate`, { method: 'POST' });
  }

  async deactivateCampaignType(id) {
    return this.request(`/campaign-types/${id}/deactivate`, { method: 'POST' });
  }

  async initializeCampaignTypes(practiceId) {
    return this.request('/campaign-types/initialize', {
      method: 'POST',
      body: JSON.stringify({ practice_id: practiceId }),
    });
  }

  async getEligibilityTypes() {
    return this.request('/campaign-types/eligibility-types');
  }

  async getRuleBuilderSchema() {
    return this.request('/campaign-types/rule-builder-schema');
  }

  async previewCampaignTypeEligibility(id, practiceId = null) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/campaign-types/${id}/preview${query}`);
  }

  async runCampaignType(id) {
    return this.request(`/campaign-types/${id}/run`, { method: 'POST' });
  }

  async runAllDynamicCampaigns(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/campaign-types/run-all${query}`, { method: 'POST' });
  }

  // Campaign Cadences
  async getCampaignCadences(campaignTypeId) {
    return this.request(`/campaign-types/${campaignTypeId}/cadences`);
  }

  async addCampaignCadence(campaignTypeId, data) {
    return this.request(`/campaign-types/${campaignTypeId}/cadences`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateCampaignCadence(campaignTypeId, cadenceId, data) {
    return this.request(`/campaign-types/${campaignTypeId}/cadences/${cadenceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteCampaignCadence(campaignTypeId, cadenceId) {
    return this.request(`/campaign-types/${campaignTypeId}/cadences/${cadenceId}`, {
      method: 'DELETE',
    });
  }

  async reorderCampaignCadences(campaignTypeId, cadenceId, newPosition) {
    return this.request(`/campaign-types/${campaignTypeId}/cadences/reorder`, {
      method: 'POST',
      body: JSON.stringify({ cadence_id: cadenceId, new_position: newPosition }),
    });
  }

  // Message Templates
  async getTemplates(practiceId = null, campaignType = null, channel = null) {
    const query = this.buildQuery({ practice_id: practiceId, campaign_type: campaignType, channel });
    return this.request(`/templates${query}`);
  }

  async getTemplate(id) {
    return this.request(`/templates/${id}`);
  }

  async previewTemplate(id, data = {}) {
    return this.request(`/templates/${id}/preview`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async createTemplate(data) {
    return this.request('/templates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTemplate(id, data) {
    return this.request(`/templates/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteTemplate(id) {
    return this.request(`/templates/${id}`, { method: 'DELETE' });
  }

  async activateTemplate(id) {
    return this.request(`/templates/${id}/activate`, { method: 'POST' });
  }

  async deactivateTemplate(id) {
    return this.request(`/templates/${id}/deactivate`, { method: 'POST' });
  }

  async createDefaultTemplates(practiceId) {
    return this.request(`/templates/practice/${practiceId}/defaults`, { method: 'POST' });
  }

  // Patients
  async getPatients(practiceIdOrOptions, options = {}) {
    // Support both old signature (limit, offset, practiceId) and new signature (practiceId, options)
    let practiceId, limit, offset, search;

    if (typeof practiceIdOrOptions === 'object' && practiceIdOrOptions !== null) {
      // Old usage: getPatients(limit, offset, practiceId) - first arg is limit (number)
      // This shouldn't happen with object, but handle edge case
      practiceId = practiceIdOrOptions.practice_id;
      limit = practiceIdOrOptions.limit;
      offset = practiceIdOrOptions.offset;
      search = practiceIdOrOptions.search;
    } else if (typeof practiceIdOrOptions === 'number' && typeof options === 'object' && options !== null && !Array.isArray(options) && (options.search !== undefined || options.limit !== undefined || options.offset !== undefined)) {
      // New usage: getPatients(practiceId, { limit, offset, search })
      practiceId = practiceIdOrOptions;
      limit = options.limit;
      offset = options.offset;
      search = options.search;
    } else if (typeof practiceIdOrOptions === 'number') {
      // Old usage: getPatients(limit, offset, practiceId)
      limit = practiceIdOrOptions;
      offset = options;
      practiceId = arguments[2] || null;
    } else {
      // Fallback: treat first arg as practiceId
      practiceId = practiceIdOrOptions;
      limit = options.limit;
      offset = options.offset;
      search = options.search;
    }

    const query = this.buildQuery({
      limit: limit || 100,
      offset: offset || 0,
      practice_id: practiceId,
      search
    });
    return this.request(`/patients${query}`);
  }

  async getPatientFiles(practiceId, patientId) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/files`);
  }

  // Signed at click time rather than at list time — the URLs the list returns
  // expire 15 minutes after the page loads.
  async getPatientFileUrl(practiceId, patientId, key) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/files/url?key=${encodeURIComponent(key)}`);
  }

  async getPatientById(id) {
    return this.request(`/patients/${id}`);
  }

  async getPatientDetail(practiceId, id) {
    return this.request(`/patients/${id}?practice_id=${practiceId}`);
  }

  async getPractice(practiceId) {
    return this.request(`/practices/${practiceId}`);
  }

  async createPatient(patientData) {
    return this.request('/patients', {
      method: 'POST',
      body: JSON.stringify(patientData),
    });
  }

  async updatePatient(id, patientData) {
    return this.request(`/patients/${id}`, {
      method: 'PUT',
      body: JSON.stringify(patientData),
    });
  }

  async optOutPatient(id, practiceId) {
    return this.request(`/patients/${id}/opt-out`, {
      method: 'POST',
      body: practiceId ? JSON.stringify({ practice_id: practiceId }) : undefined,
    });
  }

  async optInPatient(id, practiceId) {
    return this.request(`/patients/${id}/opt-in`, {
      method: 'POST',
      body: practiceId ? JSON.stringify({ practice_id: practiceId }) : undefined,
    });
  }

  async getPatientEngagement(patientId) {
    return this.request(`/reports/patient/${patientId}/engagement`);
  }

  // Appointments
  async getAppointments(practiceId, options = {}) {
    const query = this.buildQuery({
      start_date: options.startDate,
      end_date: options.endDate,
      status: options.status,
      provider_id: options.providerId,
      department_id: options.departmentId,
      provider_name: options.providerName,
      department_name: options.departmentName,
      limit: options.limit
    });
    return this.request(`/practices/${practiceId}/appointments${query}`);
  }

  async getAppointment(practiceId, appointmentId) {
    return this.request(`/practices/${practiceId}/appointments/${appointmentId}`);
  }

  // Insurance Change Requests (Phase 3 — S3 staff approval workflow)
  async getInsuranceChangeRequests(practiceId, options = {}) {
    const query = this.buildQuery({ limit: options.limit, offset: options.offset });
    return this.request(`/practices/${practiceId}/insurance-change-requests${query}`);
  }
  async getInsuranceChangeRequest(practiceId, id) {
    return this.request(`/practices/${practiceId}/insurance-change-requests/${id}`);
  }
  async approveInsuranceChangeRequest(practiceId, id) {
    return this.request(`/practices/${practiceId}/insurance-change-requests/${id}/approve`, { method: 'POST' });
  }
  async rejectInsuranceChangeRequest(practiceId, id, reason) {
    return this.request(`/practices/${practiceId}/insurance-change-requests/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason })
    });
  }

  // List a patient's full appointment history (past + present), ordered newest first
  async getPatientAppointments(practiceId, patientId) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/appointments`);
  }

  // List every active consent form for a practice, with category info attached.
  // Drives the per-form picker on the Adhoc Forms page (commons GH-49).
  async getAllConsentForms(practiceId) {
    return this.request(`/practices/${practiceId}/intake/consent-forms`);
  }

  // GH-36 — eligible source appointments for the "Transfer Intake Data" picker.
  // Returns prior appointments for the same patient that have a populated form.
  async getTransferableSources(practiceId, appointmentId) {
    return this.request(`/practices/${practiceId}/appointments/${appointmentId}/transferable-sources`);
  }

  // GH-36 — manual "Transfer Intake Data" trigger.
  async transferIntakeData(practiceId, appointmentId, fromAppointmentId) {
    return this.request(`/practices/${practiceId}/appointments/${appointmentId}/transfer-intake-data`, {
      method: 'POST',
      body: JSON.stringify({ from_appointment_id: fromAppointmentId }),
    });
  }

  // ========================================
  // Two-Way SMS Messaging
  // ========================================

  /**
   * Get SMS conversations (inbox view)
   */
  async getSmsConversations(practiceId, options = {}) {
    const query = this.buildQuery({
      practice_id: practiceId,
      limit: options.limit,
      offset: options.offset,
      has_unread: options.hasUnread,
      search: options.search,
      group: options.group,  // GH-messaging-grouping: pass group filter to backend
    });
    return this.request(`/sms/conversations${query}`);
  }

  // GH-messaging-grouping — inbox groups + conversation assign/star/category
  async getSmsInboxGroups(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/sms/inbox-groups${query}`);
  }

  async assignSmsConversation(conversationId, userId, practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/sms/conversations/${conversationId}/assign${query}`, {
      method: 'PATCH',
      body: JSON.stringify({ userId }),
    });
  }

  async setSmsConversationCategory(conversationId, category, practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/sms/conversations/${conversationId}/category${query}`, {
      method: 'PATCH',
      body: JSON.stringify({ category }),
    });
  }

  async starSmsConversation(conversationId, practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/sms/conversations/${conversationId}/star${query}`, {
      method: 'POST',
    });
  }

  async unstarSmsConversation(conversationId, practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/sms/conversations/${conversationId}/star${query}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get SMS inbox with active conversations
   */
  async getSmsInbox(practiceId, options = {}) {
    const query = this.buildQuery({
      practice_id: practiceId,
      limit: options.limit,
      offset: options.offset
    });
    return this.request(`/sms/inbox${query}`);
  }

  /**
   * Get a single conversation with messages
   */
  async getSmsConversation(conversationId, options = {}) {
    const query = this.buildQuery({
      message_limit: options.messageLimit,
      message_offset: options.messageOffset
    });
    return this.request(`/sms/conversations/${conversationId}${query}`);
  }

  /**
   * Get messages for a conversation (paginated)
   */
  async getSmsMessages(conversationId, options = {}) {
    const query = this.buildQuery({
      limit: options.limit,
      offset: options.offset
    });
    return this.request(`/sms/conversations/${conversationId}/messages${query}`);
  }

  /**
   * Send an SMS message (staff reply)
   */
  async sendSmsMessage(data) {
    return this.request('/sms/messages/send', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Start a new SMS conversation with a patient
   */
  async startSmsConversation(data) {
    return this.request('/sms/conversations/start', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Mark a conversation as read
   */
  async markSmsConversationRead(conversationId) {
    return this.request(`/sms/conversations/${conversationId}/mark-read`, {
      method: 'POST',
    });
  }

  /**
   * Get sub-conversation details
   */
  async getSmsSubConversation(subConversationId) {
    return this.request(`/sms/sub-conversations/${subConversationId}`);
  }

  /**
   * Resolve (close) a sub-conversation
   */
  async resolveSmsSubConversation(subConversationId, notes = null) {
    return this.request(`/sms/sub-conversations/${subConversationId}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  /**
   * Lookup patients by phone number (for disambiguation)
   */
  async lookupPatientsByPhone(practiceId, phone) {
    const query = this.buildQuery({ practice_id: practiceId, phone });
    return this.request(`/sms/patient-lookup${query}`);
  }

  /**
   * Get pending disambiguation messages
   */
  async getSmsPendingDisambiguations(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/sms/pending${query}`);
  }

  /**
   * Assign a pending message to a patient
   */
  async assignSmsPendingToPatient(pendingId, patientId) {
    return this.request(`/sms/pending/${pendingId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId }),
    });
  }

  /**
   * Get SMS statistics for a practice
   */
  async getSmsStats(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/sms/stats${query}`);
  }

  // =====================================================
  // SMS Consent Management (HIPAA Compliance)
  // =====================================================

  /**
   * Get patient SMS consent status
   */
  async getPatientSmsConsent(practiceId, patientId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/sms/consent/${patientId}${query}`);
  }

  /**
   * Grant SMS consent for a patient
   */
  async grantSmsConsent(practiceId, patientId, consentSource = 'intake_form') {
    return this.request(`/sms/consent/${patientId}/grant`, {
      method: 'POST',
      body: JSON.stringify({
        practice_id: practiceId,
        consent_source: consentSource
      }),
    });
  }

  /**
   * Withdraw SMS consent for a patient
   */
  async withdrawSmsConsent(practiceId, patientId, reason = null) {
    return this.request(`/sms/consent/${patientId}/withdraw`, {
      method: 'POST',
      body: JSON.stringify({
        practice_id: practiceId,
        reason
      }),
    });
  }

  /**
   * List all patients with consent status
   */
  async listSmsConsent(practiceId, includeWithdrawn = false) {
    const query = this.buildQuery({
      practice_id: practiceId,
      include_withdrawn: includeWithdrawn
    });
    return this.request(`/sms/consent${query}`);
  }

  // =====================================================
  // AI SMS Scheduling
  // =====================================================

  // =====================================================
  // Voice Workflow Builder
  // =====================================================
  async analyzeCall(practiceId, callId) {
    return this.request(`/practices/${practiceId}/calls/${callId}/analyze`, { method: 'POST' });
  }
  async listVoiceFlowTestScenarios(practiceId) {
    return this.request(`/practices/${practiceId}/voice-flows/test-scenarios`);
  }
  async runVoiceFlowTest(practiceId, flowId, scenarioId) {
    return this.request(`/practices/${practiceId}/voice-flows/${flowId}/run-test`, {
      method: 'POST',
      body: JSON.stringify({ scenario_id: scenarioId })
    });
  }
  async getVoiceFlowTestRun(practiceId, flowId, runId) {
    return this.request(`/practices/${practiceId}/voice-flows/${flowId}/run-test/${runId}`);
  }
  async listVoiceFlowVersions(practiceId, flowId) {
    return this.request(`/practices/${practiceId}/voice-flows/${flowId}/versions`);
  }
  async snapshotVoiceFlow(practiceId, flowId) {
    return this.request(`/practices/${practiceId}/voice-flows/${flowId}/snapshot`, { method: 'POST' });
  }
  async revertVoiceFlowPublished(practiceId, flowId, versionId) {
    return this.request(`/practices/${practiceId}/voice-flows/${flowId}/versions/${versionId}/promote`, { method: 'POST' });
  }
  async restoreVoiceFlowAsDraft(practiceId, flowId, versionId) {
    return this.request(`/practices/${practiceId}/voice-flows/${flowId}/versions/${versionId}/restore`, { method: 'POST' });
  }

  async getAiQueue(practiceId, options = {}) {
    const query = this.buildQuery({
      practice_id: practiceId,
      status: options.status,
      sort: options.sort,
      limit: options.limit,
      offset: options.offset
    });
    return this.request(`/ai-sms/queue${query}`);
  }

  async getAiQueueItem(id) {
    return this.request(`/ai-sms/queue/${id}`);
  }

  async resolveAiQueueItem(id) {
    return this.request(`/ai-sms/queue/${id}/resolve`, { method: 'POST' });
  }

  async replyToEmail(queueItemId, body, subject, practiceId) {
    const query = practiceId ? `?practice_id=${practiceId}` : '';
    return this.request(`/ai-sms/queue/${queueItemId}/reply-email${query}`, {
      method: 'POST',
      body: JSON.stringify({ body, subject }),
    });
  }

  async draftEmailReply(queueItemId, practiceId) {
    const query = practiceId ? `?practice_id=${practiceId}` : '';
    return this.request(`/ai-sms/queue/${queueItemId}/draft-reply${query}`, { method: 'POST' });
  }

  async getEmailThread(queueItemId, practiceId) {
    const query = practiceId ? `?practice_id=${practiceId}` : '';
    return this.request(`/ai-sms/queue/${queueItemId}/thread${query}`);
  }

  async verifySesEmail(practiceId, email) {
    return this.request(`/practices/${practiceId}/verify-email`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async aiTakeover(practiceId, subConversationId) {
    return this.request(`/ai-sms/takeover/${subConversationId}`, {
      method: 'POST',
      body: JSON.stringify({ practice_id: practiceId }),
    });
  }

  async aiReleaseTakeover(practiceId, subConversationId) {
    return this.request(`/ai-sms/takeover/${subConversationId}?practice_id=${practiceId}`, {
      method: 'DELETE',
    });
  }

  async reassignQueueItem(id, userId) {
    return this.request(`/ai-sms/queue/${id}/reassign`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async unassignQueueItem(id) {
    return this.request(`/ai-sms/queue/${id}/unassign`, {
      method: 'POST',
    });
  }

  async claimQueueItem(id) {
    return this.request(`/ai-sms/queue/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ claim_only: true }),
    });
  }

  async forceResolveQueueItem(id) {
    return this.request(`/ai-sms/queue/${id}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ force: true }),
    });
  }

  async getAiConfig(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai-sms/config${query}`);
  }

  async updateAiConfig(practiceId, data) {
    return this.request(`/ai-sms/config?practice_id=${practiceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getAiAnalytics(practiceId, options = {}) {
    const query = this.buildQuery({
      practice_id: practiceId,
      date_from: options.dateFrom,
      date_to: options.dateTo
    });
    return this.request(`/ai-sms/analytics${query}`);
  }

  async getAiIntentLogs(practiceId, options = {}) {
    const query = this.buildQuery({
      practice_id: practiceId,
      limit: options.limit,
      offset: options.offset,
      date_from: options.dateFrom,
      date_to: options.dateTo
    });
    return this.request(`/ai-sms/intent-logs${query}`);
  }

  // =====================================================
  // Voice AI Calling
  // =====================================================

  /**
   * Get voice call dashboard with stats
   */
  async getVoiceDashboard(practiceId, options = {}) {
    const query = this.buildQuery({
      date_from: options.dateFrom,
      date_to: options.dateTo
    });
    return this.request(`/practices/${practiceId}/voice/dashboard${query}`);
  }

  /**
   * Get voice call history for a practice
   */
  async getVoiceCalls(practiceId, options = {}) {
    const query = this.buildQuery({
      limit: options.limit,
      offset: options.offset,
      status: options.status,
      outcome: options.outcome,
      date_from: options.dateFrom,
      date_to: options.dateTo
    });
    return this.request(`/practices/${practiceId}/calls${query}`);
  }

  /**
   * Get a single call with details and transcript
   */
  async getVoiceCall(practiceId, callId) {
    return this.request(`/practices/${practiceId}/calls/${callId}`);
  }

  /**
   * Get calls for a specific patient
   */
  async getPatientVoiceCalls(practiceId, patientId, limit = 20) {
    const query = this.buildQuery({ limit });
    return this.request(`/practices/${practiceId}/patients/${patientId}/calls${query}`);
  }

  /**
   * Initiate a voice call to a patient
   */
  async initiateVoiceCall(practiceId, patientId, campaignType = 'reactivation') {
    return this.request(`/practices/${practiceId}/patients/${patientId}/call-now`, {
      method: 'POST',
      body: JSON.stringify({ campaign_type: campaignType }),
    });
  }

  // =====================================================
  // Voice Assistants
  // =====================================================

  /**
   * Get voice assistants for a practice
   */
  async getVoiceAssistants(practiceId, includeDisabled = false) {
    const query = this.buildQuery({ include_disabled: includeDisabled });
    return this.request(`/practices/${practiceId}/voice/assistants${query}`);
  }

  /**
   * Get voice assistant for a specific campaign type
   */
  async getVoiceAssistant(practiceId, campaignType) {
    return this.request(`/practices/${practiceId}/voice/assistants/${campaignType}`);
  }

  /**
   * Update voice assistant configuration
   */
  async updateVoiceAssistant(practiceId, campaignType, data) {
    return this.request(`/practices/${practiceId}/voice/assistants/${campaignType}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // =====================================================
  // Voice Consent
  // =====================================================

  /**
   * Get patient voice consent status
   */
  async getPatientVoiceConsent(practiceId, patientId) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/voice-consent`);
  }

  /**
   * Update patient voice consent
   */
  async updatePatientVoiceConsent(practiceId, patientId, status, source = null) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/voice-consent`, {
      method: 'PUT',
      body: JSON.stringify({ status, source }),
    });
  }

  // =====================================================
  // Callback Tasks
  // =====================================================

  /**
   * Get callback tasks for a practice
   */
  async getCallbackTasks(practiceId, options = {}) {
    const query = this.buildQuery({
      limit: options.limit,
      offset: options.offset,
      status: options.status,
      urgency: options.urgency,
      source: options.source
    });
    return this.request(`/practices/${practiceId}/callbacks${query}`);
  }

  /**
   * Get a single callback task
   */
  async getCallbackTask(practiceId, callbackId) {
    return this.request(`/practices/${practiceId}/callbacks/${callbackId}`);
  }

  /**
   * Assign callback task to a user
   */
  async assignCallbackTask(practiceId, callbackId, userId = null) {
    return this.request(`/practices/${practiceId}/callbacks/${callbackId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    });
  }

  /**
   * Complete a callback task
   */
  async completeCallbackTask(practiceId, callbackId, notes = null) {
    return this.request(`/practices/${practiceId}/callbacks/${callbackId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ notes }),
    });
  }

  /**
   * Cancel a callback task
   */
  async cancelCallbackTask(practiceId, callbackId, reason = null) {
    return this.request(`/practices/${practiceId}/callbacks/${callbackId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  // =====================================================
  // Voice Configuration
  // =====================================================

  /**
   * Get voice configuration for a practice
   */
  async getVoiceConfig(practiceId) {
    return this.request(`/practices/${practiceId}/voice/config`);
  }

  /**
   * Update voice configuration
   */
  async updateVoiceConfig(practiceId, config) {
    return this.request(`/practices/${practiceId}/voice/config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  // =====================================================
  // Voice Practice Profile (hours, address, insurances, FAQs, holidays, readiness)
  // =====================================================

  /**
   * Get practice profile + readiness checklist → { profile, readiness }
   */
  async getVoiceProfile(practiceId) {
    return this.request(`/practices/${practiceId}/voice/profile`);
  }

  /**
   * Update practice profile fields (only keys present are written)
   */
  async updateVoiceProfile(practiceId, patch) {
    return this.request(`/practices/${practiceId}/voice/profile`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    });
  }

  /**
   * Re-run the launch readiness checks → [{ key, label, ok, detail }]
   */
  async getVoiceReadiness(practiceId) {
    return this.request(`/practices/${practiceId}/voice/readiness`);
  }

  async getVoiceCapabilities(practiceId) {
    return this.request(`/practices/${practiceId}/voice/capabilities`);
  }

  async setVoiceCapabilities(practiceId, packs) {
    return this.request(`/practices/${practiceId}/voice/capabilities`, {
      method: 'PUT',
      body: JSON.stringify({ packs })
    });
  }

  async listVoiceFaqs(practiceId) {
    return this.request(`/practices/${practiceId}/voice/faqs`);
  }

  async createVoiceFaq(practiceId, data) {
    return this.request(`/practices/${practiceId}/voice/faqs`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVoiceFaq(practiceId, faqId, data) {
    return this.request(`/practices/${practiceId}/voice/faqs/${faqId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVoiceFaq(practiceId, faqId) {
    return this.request(`/practices/${practiceId}/voice/faqs/${faqId}`, {
      method: 'DELETE',
    });
  }

  async listVoiceHolidays(practiceId) {
    return this.request(`/practices/${practiceId}/voice/holidays`);
  }

  async createVoiceHoliday(practiceId, data) {
    return this.request(`/practices/${practiceId}/voice/holidays`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateVoiceHoliday(practiceId, holidayId, data) {
    return this.request(`/practices/${practiceId}/voice/holidays/${holidayId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteVoiceHoliday(practiceId, holidayId) {
    return this.request(`/practices/${practiceId}/voice/holidays/${holidayId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Generate a voice token for patient voice session
   */
  async generateVoiceToken(practiceId, patientId, campaignType) {
    return this.request(`/practices/${practiceId}/voice/generate-token`, {
      method: 'POST',
      body: JSON.stringify({ patientId, campaignType }),
    });
  }

  // =====================================================
  // Reputation Management
  // =====================================================

  /**
   * Get reputation configuration
   */
  async getReputationConfig(practiceId) {
    return this.request(`/practices/${practiceId}/reputation/config`);
  }

  /**
   * Update reputation configuration
   */
  async updateReputationConfig(practiceId, config) {
    return this.request(`/practices/${practiceId}/reputation/config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  }

  /**
   * Get reputation dashboard metrics
   */
  async getReputationDashboard(practiceId, dateFrom = null, dateTo = null) {
    const query = this.buildQuery({ date_from: dateFrom, date_to: dateTo });
    return this.request(`/practices/${practiceId}/reputation/dashboard${query}`);
  }

  /**
   * Get suppression report
   */
  async getSuppressionReport(practiceId, dateFrom = null, dateTo = null) {
    const query = this.buildQuery({ date_from: dateFrom, date_to: dateTo });
    return this.request(`/practices/${practiceId}/reputation/suppression-report${query}`);
  }

  /**
   * List feedback responses
   */
  async listFeedbackResponses(practiceId, options = {}) {
    const query = this.buildQuery({
      status: options.status,
      max_rating: options.maxRating,
      limit: options.limit,
      offset: options.offset
    });
    return this.request(`/practices/${practiceId}/reputation/feedback${query}`);
  }

  /**
   * Update feedback response status
   */
  async updateFeedbackStatus(practiceId, feedbackId, status) {
    return this.request(`/practices/${practiceId}/reputation/feedback/${feedbackId}`, {
      method: 'PATCH',
      body: JSON.stringify({ review_status: status }),
    });
  }

  /**
   * List practice locations
   */
  async listLocations(practiceId) {
    return this.request(`/practices/${practiceId}/locations`);
  }

  /**
   * Create practice location
   */
  async createLocation(practiceId, data) {
    return this.request(`/practices/${practiceId}/locations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update practice location
   */
  async updateLocation(practiceId, locationId, data) {
    return this.request(`/practices/${practiceId}/locations/${locationId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete practice location
   */
  async deleteLocation(practiceId, locationId) {
    return this.request(`/practices/${practiceId}/locations/${locationId}`, {
      method: 'DELETE',
    });
  }

  /**
   * List platform configurations
   */
  async listPlatforms(practiceId, locationId = null) {
    const query = this.buildQuery({ location_id: locationId });
    return this.request(`/practices/${practiceId}/platforms${query}`);
  }

  /**
   * Create platform configuration
   */
  async createPlatform(practiceId, data) {
    return this.request(`/practices/${practiceId}/platforms`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Update platform configuration
   */
  async updatePlatform(practiceId, platformId, data) {
    return this.request(`/practices/${practiceId}/platforms/${platformId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete platform configuration
   */
  async deletePlatform(practiceId, platformId) {
    return this.request(`/practices/${practiceId}/platforms/${platformId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get patient feedback consent
   */
  async getPatientFeedbackConsent(practiceId, patientId) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/feedback-consent`);
  }

  /**
   * Update patient feedback consent
   */
  async updatePatientFeedbackConsent(practiceId, patientId, consentType, status) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/feedback-consent`, {
      method: 'PUT',
      body: JSON.stringify({ consent_type: consentType, status }),
    });
  }

  /**
   * Request feedback for a patient (manual trigger)
   */
  async requestPatientFeedback(practiceId, patientId, appointmentId = null, immediate = true) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/request-feedback`, {
      method: 'POST',
      body: JSON.stringify({ appointment_id: appointmentId, immediate }),
    });
  }

  // =====================================================
  // Public Feedback API (for feedback landing page)
  // =====================================================

  /**
   * Get feedback page data (public - no auth)
   */
  async getFeedbackPageData(token) {
    return this.request(`/feedback/${token}`);
  }

  /**
   * Submit rating (public - no auth)
   */
  async submitFeedbackRating(token, rating) {
    return this.request(`/feedback/${token}/submit`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    });
  }

  /**
   * Submit comment for low rating (public - no auth)
   */
  async submitFeedbackComment(token, comment) {
    return this.request(`/feedback/${token}/comment`, {
      method: 'POST',
      body: JSON.stringify({ comment }),
    });
  }

  // Onboarding
  async getOnboardingStatus(practiceId) {
    return this.request(`/practices/${practiceId}/onboarding/status`);
  }

  async updateOnboardingProgress(practiceId, progress) {
    return this.request(`/practices/${practiceId}/onboarding/progress`, {
      method: 'PUT',
      body: JSON.stringify(progress),
    });
  }

  async completeOnboarding(practiceId) {
    return this.request(`/practices/${practiceId}/onboarding/complete`, {
      method: 'POST',
    });
  }

  // =====================================================
  // CONSENT MANAGEMENT
  // =====================================================

  async getConsentStats(practiceId) {
    return this.request(`/practices/${practiceId}/consents/stats`);
  }

  async getConsentPatients(practiceId, params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/consents/patients${qs ? '?' + qs : ''}`);
  }

  async getPatientConsents(practiceId, patientId) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/consents`);
  }

  // ---- Messaging batch 2 (Oct 2026): notices, consent, pending senders, summary ----
  async sendSmsConsentRequest(practiceId, patientId) {
    return this.request(`/sms/consent-request?practice_id=${practiceId}`, { method: 'POST', body: JSON.stringify({ patient_id: patientId }) });
  }

  async resendSmsPhiNotice(practiceId, conversationId) {
    return this.request(`/sms/conversations/${conversationId}/phi-notice?practice_id=${practiceId}`, { method: 'POST' });
  }

  async getSmsConversationSummary(practiceId, conversationId, refresh = false) {
    return this.request(`/sms/conversations/${conversationId}/summary?practice_id=${practiceId}${refresh ? '&refresh=true' : ''}`);
  }

  async askSmsPendingToIdentify(practiceId, pendingId) {
    return this.request(`/sms/pending/${pendingId}/ask?practice_id=${practiceId}`, { method: 'POST' });
  }

  async dismissSmsPending(practiceId, pendingId, reason) {
    return this.request(`/sms/pending/${pendingId}/dismiss?practice_id=${practiceId}`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  // ---- Secure document link (messaging thread) ----
  async getDocumentCategories() {
    return this.request('/sms/document-categories');
  }

  async createDocumentRequest(practiceId, { patientId, categories, note, conversationId }) {
    return this.request(`/sms/document-requests?practice_id=${practiceId}`, {
      method: 'POST',
      body: JSON.stringify({ patient_id: patientId, categories, note, conversation_id: conversationId }),
    });
  }

  async sendPatientDocuments(practiceId, { patientId, files, note, conversationId }) {
    const form = new FormData();
    form.append('patient_id', String(patientId));
    if (conversationId) form.append('conversation_id', String(conversationId));
    if (note) form.append('note', note);
    for (const f of files) form.append('files', f, f.name);
    const token = this.getToken();
    const res = await fetch(`${API_BASE_URL}/sms/documents/send?practice_id=${practiceId}`, {
      method: 'POST',
      headers: { ...(token && { Authorization: `Bearer ${token}` }) },
      body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.error || `Send failed (${res.status})`);
    return data;
  }

  async getPatientDocuments(practiceId, patientId) {
    return this.request(`/sms/patients/${patientId}/documents?practice_id=${practiceId}`);
  }

  async getPatientDocumentUrl(practiceId, documentId) {
    return this.request(`/sms/documents/${documentId}/url?practice_id=${practiceId}`);
  }

  async recategorizePatientDocument(practiceId, documentId, category) {
    return this.request(`/sms/documents/${documentId}?practice_id=${practiceId}`, {
      method: 'PATCH',
      body: JSON.stringify({ category }),
    });
  }

  async pushPatientDocument(practiceId, documentId) {
    return this.request(`/sms/documents/${documentId}/push?practice_id=${practiceId}`, { method: 'POST' });
  }

  // Patient side of the link (public; no auth token needed)
  async getDocLink(token) {
    return this.request(`/docs/${token}`);
  }

  async verifyDocLink(token, lastname, dob) {
    return this.request(`/docs/${token}/verify`, { method: 'POST', body: JSON.stringify({ lastname, dob }) });
  }

  async uploadDocLinkFile(token, session, category, file) {
    const form = new FormData();
    form.append('category', category);
    form.append('file', file, file.name);
    const res = await fetch(`${API_BASE_URL}/docs/${token}/upload`, {
      method: 'POST',
      headers: { 'x-doc-session': session },
      body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.success === false) throw new Error(data.error || `Upload failed (${res.status})`);
    return data;
  }

  async getDocLinkFiles(token, session) {
    return this.request(`/docs/${token}/files`, { headers: { 'x-doc-session': session } });
  }

  async updatePatientConsent(practiceId, patientId, consentType, data) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/consents/${consentType}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // =====================================================
  // Departments & Providers (shared helpers)
  // =====================================================

  async getDepartments(practiceId) {
    return this.request(`/practices/${practiceId}/voice/departments`);
  }

  async getProviders(practiceId) {
    return this.request(`/practices/${practiceId}/voice/providers`);
  }

  // =====================================================
  // NOTIFICATION CENTER
  // =====================================================

  // Rules
  async getNotificationRules(practiceId) {
    return this.request(`/practices/${practiceId}/notifications/rules`);
  }

  async getNotificationRule(practiceId, ruleId) {
    return this.request(`/practices/${practiceId}/notifications/rules/${ruleId}`);
  }

  async createNotificationRule(practiceId, data) {
    return this.request(`/practices/${practiceId}/notifications/rules`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNotificationRule(practiceId, ruleId, data) {
    return this.request(`/practices/${practiceId}/notifications/rules/${ruleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteNotificationRule(practiceId, ruleId) {
    return this.request(`/practices/${practiceId}/notifications/rules/${ruleId}`, {
      method: 'DELETE',
    });
  }

  async toggleNotificationRule(practiceId, ruleId) {
    return this.request(`/practices/${practiceId}/notifications/rules/${ruleId}/toggle`, {
      method: 'POST',
    });
  }

  async duplicateNotificationRule(practiceId, ruleId) {
    return this.request(`/practices/${practiceId}/notifications/rules/${ruleId}/duplicate`, {
      method: 'POST',
    });
  }

  async reevaluateNotificationRules(practiceId) {
    return this.request(`/practices/${practiceId}/notifications/rules/reevaluate`, {
      method: 'POST',
    });
  }

  async getNotificationReevaluateStats(practiceId) {
    return this.request(`/practices/${practiceId}/notifications/rules/reevaluate-stats`);
  }

  // Templates
  async getNotificationTemplates(practiceId, channel) {
    return this.request(`/practices/${practiceId}/notifications/templates?channel=${channel}`);
  }

  async createNotificationTemplate(practiceId, data) {
    return this.request(`/practices/${practiceId}/notifications/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateNotificationTemplate(practiceId, templateId, data) {
    return this.request(`/practices/${practiceId}/notifications/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteNotificationTemplate(practiceId, templateId, channel) {
    return this.request(`/practices/${practiceId}/notifications/templates/${templateId}?channel=${channel}`, {
      method: 'DELETE',
    });
  }

  // Appointment types
  async getNotificationAppointmentTypes(practiceId) {
    return this.request(`/practices/${practiceId}/notifications/appointment-types`);
  }

  // Stats & Activity
  async getNotificationStats(practiceId) {
    return this.request(`/practices/${practiceId}/notifications/stats`);
  }

  async getNotificationHistory(practiceId, params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/notifications/history${qs ? '?' + qs : ''}`);
  }

  async getAppointmentNotifications(practiceId, appointmentId) {
    return this.request(`/practices/${practiceId}/appointments/${appointmentId}/notifications`);
  }

  // Send Now
  async sendNotificationNow(practiceId, data) {
    return this.request(`/practices/${practiceId}/notifications/send-now`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Send Now — preview the rendered message without sending
  async previewNotificationNow(practiceId, data) {
    return this.request(`/practices/${practiceId}/notifications/preview-now`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async searchNotificationRules(practiceId, query) {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    return this.request(`/practices/${practiceId}/notifications/rules/search?${params}`);
  }

  // Search appointments (for Send Now)
  async searchAppointmentsForNotification(practiceId, params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/notifications/search-appointments${qs ? '?' + qs : ''}`);
  }

  // ── HIPAA Audit Log ──────────────────────────────────────────

  async getHIPAAAuditLogs(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') qs.set(k, v); });
    return this.request(`/admin/hipaa-audit-log?${qs}`);
  }

  async getHIPAAAuditLogFilters() {
    return this.request('/admin/hipaa-audit-log/filters');
  }

  async getHIPAAAuditLogDetail(id) {
    return this.request(`/admin/hipaa-audit-log/${id}`);
  }

  // ── Operational Logs (Shared cross-system logs) ──────────
  async getOperationalLogs(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') qs.set(k, v);
    });
    return this.request(`/logs${qs.toString() ? '?' + qs : ''}`);
  }

  async getLogTrace(correlationId) {
    return this.request(`/logs/trace/${correlationId}`);
  }

  async getLogStats(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== '') qs.set(k, v);
    });
    return this.request(`/logs/stats${qs.toString() ? '?' + qs : ''}`);
  }

  async getHIPAAPatientHistory(patientId, params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/admin/hipaa-audit-log/patient/${patientId}${qs ? '?' + qs : ''}`);
  }

  async verifyAuditChain(params = {}) {
    return this.request('/admin/hipaa-audit-log/verify-chain', {
      method: 'POST',
      body: JSON.stringify(params)
    });
  }

  getHIPAAAuditExportURL(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') qs.set(k, v); });
    return `${API_BASE_URL}/admin/hipaa-audit-log/export?${qs}`;
  }

  // ── BAA Management (Super Admin) ─────────────────────
  async getBAAs(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') qs.set(k, v); });
    return this.request(`/admin/baas?${qs}`);
  }

  async getBAA(id) {
    return this.request(`/admin/baas/${id}`);
  }

  async createBAA(formData) {
    const token = this.getToken();
    const res = await fetch(`${API_BASE_URL}/admin/baas`, {
      method: 'POST',
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create BAA');
    return data;
  }

  async updateBAA(id, formData) {
    const token = this.getToken();
    const res = await fetch(`${API_BASE_URL}/admin/baas/${id}`, {
      method: 'PUT',
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to update BAA');
    return data;
  }

  async deleteBAA(id) {
    return this.request(`/admin/baas/${id}`, { method: 'DELETE' });
  }

  async downloadBAADocument(id) {
    return this.request(`/admin/baas/${id}/download`);
  }

  async uploadBAADocument(id, file) {
    const formData = new FormData();
    formData.append('document', file);
    const token = this.getToken();
    const res = await fetch(`${API_BASE_URL}/admin/baas/${id}/upload`, {
      method: 'POST',
      headers: { ...(token && { 'Authorization': `Bearer ${token}` }) },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to upload document');
    return data;
  }

  async getExpiringBAAs(days = 30) {
    return this.request(`/admin/baas/expiring?days=${days}`);
  }

  // ── Patient Timeline ──

  async getPatientTimeline(practiceId, patientId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/patients/${patientId}/timeline?${query}`);
  }

  async getPatientInteractionStats(practiceId, patientId) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/interaction-stats`);
  }

  async getPatientAiSummary(practiceId, patientId, context = 'detail', refresh = false) {
    const params = { context };
    if (refresh) params.refresh = 'true';
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/patients/${patientId}/ai-summary?${query}`);
  }

  async getActivityFeed(practiceId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/activity-feed?${query}`);
  }
  // ── Digital Forms Intake ──

  async getIntakeQuestionCategories(practiceId) {
    return this.request(`/practices/${practiceId}/intake/question-categories`);
  }

  async createIntakeQuestionCategory(practiceId, data) {
    return this.request(`/practices/${practiceId}/intake/question-categories`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  async updateIntakeQuestionCategory(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/intake/question-categories/${id}`, {
      method: 'PUT', body: JSON.stringify(data)
    });
  }

  async deleteIntakeQuestionCategory(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/question-categories/${id}`, { method: 'DELETE' });
  }

  async getIntakeQuestions(practiceId, categoryId) {
    return this.request(`/practices/${practiceId}/intake/question-categories/${categoryId}/questions`);
  }

  async createIntakeQuestion(practiceId, data) {
    return this.request(`/practices/${practiceId}/intake/questions`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  async updateIntakeQuestion(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/intake/questions/${id}`, {
      method: 'PUT', body: JSON.stringify(data)
    });
  }

  async deleteIntakeQuestion(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/questions/${id}`, { method: 'DELETE' });
  }

  async getIntakeFormCategories(practiceId) {
    return this.request(`/practices/${practiceId}/intake/form-categories`);
  }

  async createIntakeFormCategory(practiceId, data) {
    return this.request(`/practices/${practiceId}/intake/form-categories`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  async updateIntakeFormCategory(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/intake/form-categories/${id}`, {
      method: 'PUT', body: JSON.stringify(data)
    });
  }

  async deleteIntakeFormCategory(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/form-categories/${id}`, { method: 'DELETE' });
  }

  async getIntakeConsentForms(practiceId, categoryId) {
    return this.request(`/practices/${practiceId}/intake/form-categories/${categoryId}/forms`);
  }

  async createIntakeConsentForm(practiceId, data) {
    return this.request(`/practices/${practiceId}/intake/consent-forms`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  async updateIntakeConsentForm(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/intake/consent-forms/${id}`, {
      method: 'PUT', body: JSON.stringify(data)
    });
  }

  async deleteIntakeConsentForm(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/consent-forms/${id}`, { method: 'DELETE' });
  }

  async getIntakePackages(practiceId) {
    return this.request(`/practices/${practiceId}/intake/packages`);
  }

  async getIntakePackage(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/packages/${id}`);
  }

  async createIntakePackage(practiceId, data) {
    return this.request(`/practices/${practiceId}/intake/packages`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  async updateIntakePackage(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/intake/packages/${id}`, {
      method: 'PUT', body: JSON.stringify(data)
    });
  }

  async deleteIntakePackage(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/packages/${id}`, { method: 'DELETE' });
  }

  async getEHRAppointmentTypes(practiceId) {
    return this.request(`/practices/${practiceId}/ehr/appointment-types`);
  }

  async getIntakeMappings(practiceId) {
    return this.request(`/practices/${practiceId}/intake/mappings`);
  }

  async createIntakeMapping(practiceId, data) {
    return this.request(`/practices/${practiceId}/intake/mappings`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  async updateIntakeMapping(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/intake/mappings/${id}`, {
      method: 'PUT', body: JSON.stringify(data)
    });
  }

  async deleteIntakeMapping(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/mappings/${id}`, { method: 'DELETE' });
  }

  async getIntakeGenerationLogs(practiceId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/intake/generations?${query}`);
  }

  async getIntakeGenerationLog(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/generations/${id}`);
  }

  async regenerateIntakeForm(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/generations/${id}/regenerate`, { method: 'POST' });
  }

  async previewIntakeJSON(practiceId, appointmentType) {
    return this.request(`/practices/${practiceId}/intake/generate-preview`, {
      method: 'POST', body: JSON.stringify({ appointment_type: appointmentType })
    });
  }

  async generateAiIntakeQuestions(practiceId, data) {
    return this.request(`/practices/${practiceId}/intake/ai-generate-questions`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  // ── Intake Templates ────────────────────────────────────

  async getIntakeTemplates(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/intake/templates${query ? '?' + query : ''}`);
  }

  async getIntakeTemplate(practiceId, templateId) {
    return this.request(`/practices/${practiceId}/intake/templates/${templateId}`);
  }

  async importIntakeTemplate(practiceId, templateId) {
    return this.request(`/practices/${practiceId}/intake/templates/import`, {
      method: 'POST', body: JSON.stringify({ template_id: templateId })
    });
  }

  // ── Ad-Hoc Intake Forms ──────────────────────────────────

  async createIntakeAdhoc(practiceId, data) {
    return this.request(`/practices/${practiceId}/intake/adhoc`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  async getIntakeAdhocRequests(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/intake/adhoc${query ? '?' + query : ''}`);
  }

  async getIntakeAdhocStats(practiceId) {
    return this.request(`/practices/${practiceId}/intake/adhoc/stats`);
  }

  async getIntakeAdhocRequest(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/adhoc/${id}`);
  }

  async sendIntakeAdhocReminder(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/adhoc/${id}/remind`, { method: 'POST' });
  }

  async cancelIntakeAdhoc(practiceId, id) {
    return this.request(`/practices/${practiceId}/intake/adhoc/${id}/cancel`, { method: 'POST' });
  }

  // ── Intake Command Center Dashboard ─────────────────────

  async getIntakeDashboardKPIs(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/intake/dashboard/kpis${query ? '?' + query : ''}`);
  }

  async getIntakeDashboardCharts(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/intake/dashboard/charts${query ? '?' + query : ''}`);
  }

  async getIntakeDashboardActiveForms(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/intake/dashboard/active-forms${query ? '?' + query : ''}`);
  }

  async getIntakeDashboardAlerts(practiceId) {
    return this.request(`/practices/${practiceId}/intake/dashboard/alerts`);
  }

  async sendIntakeBulkRemind(practiceId, formIds) {
    return this.request(`/practices/${practiceId}/intake/dashboard/bulk-remind`, {
      method: 'POST', body: JSON.stringify({ form_ids: formIds })
    });
  }

  async exportIntakeDashboardCSV(practiceId, params = {}) {
    // GH-44 follow-up: this used to read `this.baseURL` and `this.token`,
    // neither of which exist on the class — URL became "undefined/practices/…"
    // which the SPA catch-all served back as index.html. Browser then
    // downloaded the React shell as "intake-forms.csv". Fix: use API_BASE_URL
    // + the same token getter as request().
    const query = new URLSearchParams(params).toString();
    const url = `${API_BASE_URL}/practices/${practiceId}/intake/dashboard/export${query ? '?' + query : ''}`;
    const token = this.getToken();
    const resp = await fetch(url, {
      headers: { ...(token && { Authorization: `Bearer ${token}` }) }
    });
    if (!resp.ok) {
      // Surface a real error instead of returning the error page as a blob —
      // otherwise the user "downloads" the error and never knows what failed.
      const text = await resp.text();
      throw new Error(`Export failed: ${resp.status} ${resp.statusText}: ${text.slice(0, 200)}`);
    }
    return resp.blob();
  }

  // ── Patient Flow Dashboard ─────────────────────────────

  async getFlowSnapshot(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/flow/snapshot${query ? '?' + query : ''}`);
  }

  async getFlowStageDetail(practiceId, stage, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/flow/stage/${stage}${query ? '?' + query : ''}`);
  }

  async getFlowTrend(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/flow/trend${query ? '?' + query : ''}`);
  }

  async getFlowTimings(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/flow/timings${query ? '?' + query : ''}`);
  }

  async postFlowTransition(practiceId, data) {
    return this.request(`/practices/${practiceId}/flow/transition`, {
      method: 'POST', body: JSON.stringify(data)
    });
  }

  // ── Guided Adoption Layer ─────────────────────────────

  async getAdoptionPageContent(page) {
    return this.request(`/adoption/page-content?page=${encodeURIComponent(page)}`);
  }

  async postAdoptionEvent(data) {
    return this.request('/adoption/events', { method: 'POST', body: JSON.stringify(data) });
  }

  async getAdoptionProgress() {
    return this.request('/adoption/my-progress');
  }

  async searchAdoptionKnowledge(query) {
    return this.request(`/adoption/search?q=${encodeURIComponent(query)}`);
  }

  async getAdoptionArticles(category) {
    const params = category ? `?category=${encodeURIComponent(category)}` : '';
    return this.request(`/adoption/articles${params}`);
  }

  async getAdoptionArticle(slug) {
    return this.request(`/adoption/articles/${slug}`);
  }

  async getAdoptionWalkthroughs(practiceId) {
    return this.request(`/practices/${practiceId}/adoption/walkthroughs`);
  }

  async createAdoptionWalkthrough(practiceId, data) {
    return this.request(`/practices/${practiceId}/adoption/walkthroughs`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateAdoptionWalkthrough(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/adoption/walkthroughs/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteAdoptionWalkthrough(practiceId, id) {
    return this.request(`/practices/${practiceId}/adoption/walkthroughs/${id}`, { method: 'DELETE' });
  }

  // ── AI Assistant ──────────────────────────────────────

  async askAiAssistant(query, currentPage, practiceId = null, history = []) {
    return this.request('/adoption/ai-assistant/ask', {
      method: 'POST',
      body: JSON.stringify({ query, current_page: currentPage, practice_id: practiceId, history }),
    });
  }

  async submitAiAssistantFeedback(queryId, feedback, comment) {
    return this.request('/adoption/ai-assistant/feedback', {
      method: 'POST',
      body: JSON.stringify({ query_id: queryId, feedback, comment }),
    });
  }

  async getAiAssistantHistory(limit = 20) {
    return this.request(`/adoption/ai-assistant/history?limit=${limit}`);
  }

  // ── Ambient Scribe ──────────────────────────────────────

  async getScribeConfig(practiceId) {
    return this.request(`/practices/${practiceId}/scribe/config`);
  }

  async getScribeSessions(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/scribe/sessions${query ? '?' + query : ''}`);
  }

  async getScribeSession(practiceId, sessionId) {
    return this.request(`/practices/${practiceId}/scribe/sessions/${sessionId}`);
  }

  async getScribeTranscript(practiceId, sessionId) {
    return this.request(`/practices/${practiceId}/scribe/sessions/${sessionId}/transcript`);
  }

  async getScribeNotes(practiceId, sessionId) {
    return this.request(`/practices/${practiceId}/scribe/sessions/${sessionId}/notes`);
  }

  async updateScribeNotes(practiceId, sessionId, sections) {
    return this.request(`/practices/${practiceId}/scribe/sessions/${sessionId}/notes`, {
      method: 'PUT', body: JSON.stringify({ sections })
    });
  }

  async approveScribeNotes(practiceId, sessionId) {
    return this.request(`/practices/${practiceId}/scribe/sessions/${sessionId}/approve`, {
      method: 'POST'
    });
  }

  async rejectScribeNotes(practiceId, sessionId) {
    return this.request(`/practices/${practiceId}/scribe/sessions/${sessionId}/reject`, {
      method: 'POST'
    });
  }

  async getScribeStats(practiceId, params = {}) {
    const query = new URLSearchParams(params).toString();
    return this.request(`/practices/${practiceId}/scribe/stats${query ? '?' + query : ''}`);
  }

  async pushScribeNotesToEHR(practiceId, sessionId) {
    return this.request(`/practices/${practiceId}/scribe/sessions/${sessionId}/push-to-ehr`, {
      method: 'POST'
    });
  }

  async pushSingleEHR(practiceId, genLogId, pushLogId) {
    return this.request(`/practices/${practiceId}/intake/generations/${genLogId}/push-single`, {
      method: 'POST',
      body: JSON.stringify({ pushLogId }),
    });
  }

  async deleteScribeSession(practiceId, sessionId) {
    return this.request(`/practices/${practiceId}/scribe/sessions/${sessionId}`, {
      method: 'DELETE'
    });
  }
  // ─── Compliance Engine — Platform Layer ────────────────
  async getComplianceControls(params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/admin/compliance/controls${query}`);
  }

  async triggerComplianceValidation() {
    return this.request('/admin/compliance/validation/run', { method: 'POST' });
  }

  async getLatestValidation() {
    return this.request('/admin/compliance/validation/latest');
  }

  async getValidationHistory(limit = 30) {
    return this.request(`/admin/compliance/validation/history?limit=${limit}`);
  }

  async getComplianceDrift() {
    return this.request('/admin/compliance/drift');
  }

  async getAiGovernanceMetrics(params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/admin/compliance/ai-governance${query}`);
  }

  async getComplianceScoresOverview() {
    return this.request('/admin/compliance/scores/overview');
  }

  // ─── Compliance Engine — Practice Layer ───────────────
  async getComplianceDashboard(practiceId) {
    return this.request(`/practices/${practiceId}/compliance/dashboard`);
  }

  async getPracticeComplianceControls(practiceId) {
    return this.request(`/practices/${practiceId}/compliance/controls`);
  }

  async getComplianceScoreTrend(practiceId, months = 12) {
    return this.request(`/practices/${practiceId}/compliance/scores/trend?months=${months}`);
  }

  async calculateComplianceScore(practiceId) {
    return this.request(`/practices/${practiceId}/compliance/scores/calculate`, { method: 'POST' });
  }

  async getComplianceGaps(practiceId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/compliance/gaps${query}`);
  }

  async getRiskAssessments(practiceId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/compliance/risk-assessments${query}`);
  }

  async createRiskAssessment(practiceId, data) {
    return this.request(`/practices/${practiceId}/compliance/risk-assessments`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateRiskAssessmentProgress(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/compliance/risk-assessments/${id}/progress`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async completeRiskAssessment(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/compliance/risk-assessments/${id}/complete`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async deleteRiskAssessment(practiceId, id) {
    return this.request(`/practices/${practiceId}/compliance/risk-assessments/${id}`, {
      method: 'DELETE',
    });
  }

  async getTrainingRecords(practiceId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/compliance/training${query}`);
  }

  async createTrainingRecord(practiceId, data) {
    return this.request(`/practices/${practiceId}/compliance/training`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async getComplianceVendors(practiceId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/compliance/vendors${query}`);
  }

  async createComplianceVendor(practiceId, data) {
    return this.request(`/practices/${practiceId}/compliance/vendors`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateComplianceVendor(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/compliance/vendors/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async getComplianceIncidents(practiceId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/compliance/incidents${query}`);
  }

  async createComplianceIncident(practiceId, data) {
    return this.request(`/practices/${practiceId}/compliance/incidents`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateComplianceIncident(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/compliance/incidents/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async resolveComplianceIncident(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/compliance/incidents/${id}/resolve`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async getComplianceAttestations(practiceId, params = {}) {
    const query = this.buildQuery(params);
    return this.request(`/practices/${practiceId}/compliance/attestations${query}`);
  }

  async createComplianceAttestation(practiceId, data) {
    return this.request(`/practices/${practiceId}/compliance/attestations`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async createRemediationTask(practiceId, data) {
    return this.request(`/practices/${practiceId}/compliance/tasks`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  async updateRemediationTask(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/compliance/tasks/${id}`, {
      method: 'PUT', body: JSON.stringify(data),
    });
  }

  async completeRemediationTask(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/compliance/tasks/${id}/complete`, {
      method: 'POST', body: JSON.stringify(data),
    });
  }

  // ─── Audit Export ─────────────────────────────────────
  async getAuditPackage(practiceId) {
    return this.request(`/practices/${practiceId}/compliance/audit-export`);
  }

  async getAuditCSVUrl(practiceId) {
    const token = this.getToken();
    return `${API_BASE_URL}/practices/${practiceId}/compliance/audit-export/csv?token=${token}`;
  }

  async getAuditSummary(practiceId) {
    return this.request(`/practices/${practiceId}/compliance/audit-export/summary`);
  }

  // ── Test Mode ──

  async getTestModeStatus() {
    return this.request('/config/test-mode');
  }

  // =====================================================
  // AI INSIGHTS
  // =====================================================

  async getAiDashboard(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/dashboard${query}`);
  }

  async getAiFeatureConfigs(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/config${query}`);
  }

  async updateAiFeatureConfig(practiceId, featureType, data) {
    return this.request(`/ai/config/${featureType}?practice_id=${practiceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // No-Show Risk
  async getNoShowRisk(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/no-show-risk${query}`);
  }

  async computeNoShowRisk(practiceId) {
    return this.request(`/ai/no-show-risk/compute?practice_id=${practiceId}`, { method: 'POST' });
  }

  // Churn Risk
  async getChurnRisk(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/churn-risk${query}`);
  }

  async getChurnDistribution(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/churn-risk/distribution${query}`);
  }

  // Revenue Forecast
  async getRevenueForecast(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/revenue-forecast${query}`);
  }

  // Sentiment
  async getFrustratedPatients(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/sentiment/frustrated${query}`);
  }

  async getSentimentSummary(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/sentiment/summary${query}`);
  }

  // Patient LTV
  async getPatientLtv(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/patient-ltv${query}`);
  }

  async getLtvDistribution(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/patient-ltv/distribution${query}`);
  }

  // No-Show Risk for single appointment
  async getNoShowRiskForAppointment(practiceId, appointmentId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/no-show-risk/${appointmentId}${query}`);
  }

  // Send-Time
  async getPatientSendTime(practiceId, patientId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/send-time/${patientId}${query}`);
  }

  // Channel Preference
  async getChannelPreference(practiceId, patientId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/channel-preference/${patientId}${query}`);
  }

  // Personalize Message
  async personalizeMessage(practiceId, data) {
    return this.request(`/ai/personalize-message?practice_id=${practiceId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Patient Sentiment
  async getPatientSentiment(practiceId, patientId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/sentiment/${patientId}${query}`);
  }

  // Scheduling
  async getRecommendedSlots(practiceId, patientId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/scheduling/${patientId}/recommended-slots${query}`);
  }

  async getSchedulingPreferences(practiceId, patientId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/scheduling/${patientId}/preferences${query}`);
  }

  // Reputation AI Responses
  async generateAiReviewResponse(practiceId, feedbackResponseId, tone = 'empathetic') {
    return this.request(`/ai/reputation/draft-response?practice_id=${practiceId}`, {
      method: 'POST',
      body: JSON.stringify({ feedbackResponseId, tone }),
    });
  }

  async getAiReviewResponses(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/reputation/responses${query}`);
  }

  async updateAiReviewResponse(practiceId, responseId, data) {
    return this.request(`/ai/reputation/responses/${responseId}?practice_id=${practiceId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async regenerateAiReviewResponse(practiceId, responseId, tone) {
    return this.request(`/ai/reputation/responses/${responseId}/regenerate?practice_id=${practiceId}`, {
      method: 'POST',
      body: JSON.stringify({ tone }),
    });
  }

  // Campaign Optimization
  async getCampaignOptimizations(practiceId, params = {}) {
    const query = this.buildQuery({ practice_id: practiceId, ...params });
    return this.request(`/ai/campaign-optimization${query}`);
  }

  async analyzeCampaignPerformance(practiceId) {
    return this.request(`/ai/campaign-optimization/analyze?practice_id=${practiceId}`, {
      method: 'POST',
    });
  }

  async applyCampaignOptimization(practiceId, optimizationId) {
    return this.request(`/ai/campaign-optimization/${optimizationId}/apply?practice_id=${practiceId}`, {
      method: 'POST',
    });
  }

  // Call Prediction
  async getCallPrediction(practiceId, patientId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/call-prediction/${patientId}${query}`);
  }

  // Revenue Forecast Accuracy
  async getForecastAccuracy(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/revenue-forecast/accuracy${query}`);
  }

  // Patient LTV Detail
  async getPatientLtvDetail(practiceId, patientId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/patient-ltv/${patientId}${query}`);
  }

  // Call Prediction Accuracy
  async getCallPredictionAccuracy(practiceId) {
    const query = this.buildQuery({ practice_id: practiceId });
    return this.request(`/ai/call-prediction/accuracy${query}`);
  }

  // ── Tags & Labels ──────────────────────────────────────────

  async getTagGroups(practiceId) {
    return this.request(`/practices/${practiceId}/tags/groups`);
  }

  async createTagGroup(practiceId, data) {
    return this.request(`/practices/${practiceId}/tags/groups`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTagGroup(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/tags/groups/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteTagGroup(practiceId, id) {
    return this.request(`/practices/${practiceId}/tags/groups/${id}`, { method: 'DELETE' });
  }

  async getTagDefinitions(practiceId, entityType = null, includeInactive = false) {
    const params = [];
    if (entityType) params.push(`entity_type=${entityType}`);
    if (includeInactive) params.push('include_inactive=1');
    const qs = params.length > 0 ? `?${params.join('&')}` : '';
    return this.request(`/practices/${practiceId}/tags/definitions${qs}`);
  }

  async createTagDefinition(practiceId, data) {
    return this.request(`/practices/${practiceId}/tags/definitions`, { method: 'POST', body: JSON.stringify(data) });
  }

  async updateTagDefinition(practiceId, id, data) {
    return this.request(`/practices/${practiceId}/tags/definitions/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteTagDefinition(practiceId, id) {
    return this.request(`/practices/${practiceId}/tags/definitions/${id}`, { method: 'DELETE' });
  }

  async getEntityTags(practiceId, entityType, entityId) {
    return this.request(`/practices/${practiceId}/tags/${entityType}/${entityId}`);
  }

  async assignTag(practiceId, entityType, entityId, tagDefinitionId) {
    return this.request(`/practices/${practiceId}/tags/${entityType}/${entityId}`, {
      method: 'POST', body: JSON.stringify({ tag_definition_id: tagDefinitionId })
    });
  }

  async removeTag(practiceId, entityType, entityId, tagDefinitionId) {
    return this.request(`/practices/${practiceId}/tags/${entityType}/${entityId}/${tagDefinitionId}`, { method: 'DELETE' });
  }

  async setEntityTags(practiceId, entityType, entityId, tagDefinitionIds) {
    return this.request(`/practices/${practiceId}/tags/${entityType}/${entityId}`, {
      method: 'PUT', body: JSON.stringify({ tag_definition_ids: tagDefinitionIds })
    });
  }

  async batchGetTags(practiceId, entityType, entityIds) {
    return this.request(`/practices/${practiceId}/tags/batch`, {
      method: 'POST', body: JSON.stringify({ entity_type: entityType, entity_ids: entityIds })
    });
  }

  async getPatientPaymentMethods(practiceId, patientId) {
    return this.request(`/practices/${practiceId}/patients/${patientId}/payment-methods`);
  }

  // ── Test Mode ──────────────────────────────────────
  async getTestMode(practiceId) {
    return this.request(`/practices/${practiceId}/test-mode`);
  }
  async updateTestMode(practiceId, data) {
    return this.request(`/practices/${practiceId}/test-mode`, { method: 'PUT', body: JSON.stringify(data) });
  }

  // ── V1→V2 Migration ──────────────────────────────────────
  async migrationScan() {
    return this.request('/migration/scan', { method: 'POST' });
  }

  // ─── Phone Management (super_admin) ───────────────────────
  async phoneEnvInfo() {
    return this.request('/admin/phones/env-info');
  }
  async phoneInventory(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/admin/phones/inventory${qs ? '?' + qs : ''}`);
  }
  async phoneInventoryOne(id) {
    return this.request(`/admin/phones/inventory/${id}`);
  }
  async phoneByPractice() {
    return this.request('/admin/phones/by-practice');
  }
  async phoneSync() {
    return this.request('/admin/phones/sync', { method: 'POST' });
  }
  async phoneDrift() {
    return this.request('/admin/phones/drift');
  }
  async phoneListRequests(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/admin/phones/requests${qs ? '?' + qs : ''}`);
  }
  async phoneGetRequest(id) {
    return this.request(`/admin/phones/requests/${id}`);
  }
  async phoneCreateRequest(body) {
    return this.request('/admin/phones/requests', { method: 'POST', body: JSON.stringify(body) });
  }
  async phoneApproveRequest(id, notes) {
    return this.request(`/admin/phones/requests/${id}/approve`, { method: 'POST', body: JSON.stringify({ notes }) });
  }
  async phoneRejectRequest(id, notes) {
    return this.request(`/admin/phones/requests/${id}/reject`, { method: 'POST', body: JSON.stringify({ notes }) });
  }
  async phoneCancelRequest(id) {
    return this.request(`/admin/phones/requests/${id}/cancel`, { method: 'POST' });
  }
  async phoneAudit(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/admin/phones/audit${qs ? '?' + qs : ''}`);
  }
  async migrationList(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/migration/list${qs ? '?' + qs : ''}`);
  }
  async migrationCounts(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return this.request(`/migration/counts${qs ? '?' + qs : ''}`);
  }
  async migrationApprove(ids) {
    return this.request('/migration/approve', { method: 'POST', body: JSON.stringify({ ids }) });
  }
  async migrationReject(ids) {
    return this.request('/migration/reject', { method: 'POST', body: JSON.stringify({ ids }) });
  }
  async migrationExecute(ids) {
    return this.request('/migration/execute', { method: 'POST', body: JSON.stringify({ ids }) });
  }
  async migrationRetry(ids) {
    return this.request('/migration/retry', { method: 'POST', body: JSON.stringify({ ids }) });
  }
  async migrationRemerge(ids) {
    return this.request('/migration/remerge', { method: 'POST', body: JSON.stringify({ ids }) });
  }
  async migrationManualMatch(id, matchData) {
    return this.request(`/migration/match/${id}`, { method: 'POST', body: JSON.stringify(matchData) });
  }
}

const apiService = new ApiService();
export default apiService;
